import { ArrowUpRight } from "lucide-react";
import { useT } from "@/i18n/I18nProvider";
import { SEO } from "@/components/common/SEO";
import { PageHero } from "@/components/common/PageHero";
import { ContactForm } from "@/components/forms/ContactForm";
import { COMPANY } from "@/data/company";

export default function Contact() {
  const { t, locale } = useT();

  const channels: Array<{ label: string; value: string; href: string; testId: string }> = [
    { label: t.contact.emailLabel, value: COMPANY.email, href: `mailto:${COMPANY.email}`, testId: "contact-email" },
    { label: t.contact.phoneLabel, value: COMPANY.phone, href: COMPANY.phoneHref, testId: "contact-phone" },
    { label: t.contact.pecLabel, value: COMPANY.pec, href: `mailto:${COMPANY.pec}`, testId: "contact-pec" },
    { label: t.contact.websiteLabel, value: COMPANY.websiteLabel, href: COMPANY.website, testId: "contact-website" },
  ];

  return (
    <>
      <SEO routeKey="contact" />
      <PageHero routeKey="contact" compact />

      <section className="container-bnm py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7">
            <ContactForm />
          </div>

          <aside className="lg:col-span-5 flex flex-col gap-12 lg:pl-8 lg:border-l border-bone-500">
            <div>
              <p className="eyebrow mb-4">{t.contact.officeEyebrow}</p>
              <h3 className="font-display font-light text-3xl text-ink mb-3">
                {t.contact.officeTitle}
              </h3>
              <address className="not-italic text-slate2 leading-relaxed" data-testid="contact-office-address">
                {COMPANY.address.street}
                <br />
                {COMPANY.address.city}
                <br />
                {COMPANY.address.country[locale]}
              </address>
              <a
                href={COMPANY.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-eyebrow text-ink hover:text-clay transition-colors border-b border-ink hover:border-clay pb-0.5"
                data-testid="contact-maps-link"
              >
                {t.contact.mapsLink}
                <ArrowUpRight size={12} />
              </a>
            </div>

            <div>
              <p className="eyebrow mb-4">{t.contact.channelsEyebrow}</p>
              <dl className="flex flex-col gap-4">
                {channels.map((c) => (
                  <div key={c.testId} className="flex items-baseline justify-between gap-4 border-b border-bone-500 pb-3">
                    <dt className="font-mono text-[11px] uppercase tracking-eyebrow text-slate2 shrink-0">
                      {c.label}
                    </dt>
                    <dd className="text-sm text-right">
                      <a
                        href={c.href}
                        className="text-ink hover:text-clay transition-colors"
                        {...(c.href.startsWith("http")
                          ? { target: "_blank", rel: "noopener noreferrer" }
                          : {})}
                        data-testid={c.testId}
                      >
                        {c.value}
                      </a>
                    </dd>
                  </div>
                ))}
                <div className="flex items-baseline justify-between gap-4 border-b border-bone-500 pb-3">
                  <dt className="font-mono text-[11px] uppercase tracking-eyebrow text-slate2 shrink-0">
                    {t.contact.hoursLabel}
                  </dt>
                  <dd className="text-slate2 text-sm text-right leading-relaxed" data-testid="contact-hours">
                    {t.contact.hoursWeekdays}
                    <br />
                    {t.contact.hoursWeekend}
                    <br />
                    <span className="text-slate2/70 text-xs">{t.contact.hoursTimezone}</span>
                  </dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
